/**
 * The business profile: the ONE write path for a company's own row.
 *
 * WHY THIS SERVICE EXISTS NOW
 * --------------------------
 * The `Business` row was the only row in the app with no way to create or edit
 * one. It could be written by the 1.6.x importer and read by `businessReader`
 * for the letterhead — the worst combination in a billing app: the details
 * that end up on a tax invoice could be set exactly once, by a migration, and
 * never corrected. A customer who fixes a typo in their GSTIN would otherwise
 * have to re-import their whole history.
 *
 * It follows the same atomicity rule as the party, document and payment paths:
 * the row and its outbox entry are written in ONE transaction, or neither is.
 *
 * THE STATE CODE IS VALIDATED, NOT TRUSTED
 * ----------------------------------------
 * `stateCode` is what decides INTRA vs INTER, and therefore CGST+SGST vs IGST.
 * `core/gst/stateCodes` exists precisely so an unrecognised state cannot be
 * stored, so this service reuses `isValidStateCode` rather than accepting a
 * free-typed string.
 *
 * AND THE GSTIN MUST AGREE WITH IT
 * --------------------------------
 * A GSTIN's first two characters ARE the state. A row holding
 * `stateCode: '29'` beside `gstin: '36AAAAA1234A1Z5'` would compute intra-state
 * tax while printing a Telangana GSTIN, and every invoice issued from it would
 * be wrong in a way no totals check would catch. So the mismatch is refused at
 * the point of entry rather than discovered at filing time.
 */
import type { Table } from 'dexie';
import { ulid } from '@platform/ids';
import { isGstinShaped, isValidStateCode, stateNameFor } from '@core/gst/stateCodes';
import type { BankAccount, Business, DocumentSettings, SyncOutboxEntry } from '../db/types';
import { recordBusinessMutation, nextVersion } from '../sync/recordBusinessMutation';

/** The tables a business write may touch. `syncOutbox` is required, not optional. */
export interface BusinessTx {
  businesses: Table<Business, string>;
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

/**
 * The document defaults a new business starts with.
 *
 * Exported so there is exactly ONE definition of "a new company's defaults".
 * The 1.6.x importer carried its own private copy of these values, which is how
 * a business created by migration and a business created by hand end up with
 * different rounding settings and therefore different invoice totals. The
 * importer imports this instead.
 */
export const DEFAULT_DOCUMENT_SETTINGS: DocumentSettings = {
  roundOffEnabled: true,
  roundOffMode: 'NEAREST_RUPEE',
  allowInvoiceWithoutDelivery: true,
  // False is the load-bearing one: an issued document is corrected, not edited.
  allowEditIssuedDocuments: false,
  showBankDetails: true,
  showSignature: false,
  thermalPrintableWidthMm: 48,
};

/** The financial-year start months worth offering. All four are real GST years. */
export const FY_START_MONTHS: readonly { value: number; label: string }[] = [
  { value: 1, label: 'January' },
  { value: 4, label: 'April' },
  { value: 7, label: 'July' },
  { value: 10, label: 'October' },
];

/** Everything a business profile can be set from. */
export interface BusinessProfileInput {
  /** The legal name that prints on the letterhead. */
  readonly legalName: string;
  readonly tradeName?: string;
  readonly gstin?: string;
  readonly pan?: string;
  /** Two-digit GST state code. Decides INTRA vs INTER. */
  readonly stateCode: string;
  readonly addressLines: readonly string[];
  readonly city: string;
  readonly district?: string;
  readonly pincode?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  readonly upiId?: string;
  /**
   * Attachment ids, ALWAYS supplied as a string by the form. '' normalises to
   * undefined, which means "this business has no logo" - and unlike the optional
   * TEXT fields, `updateBusiness` applies these two unconditionally, because they
   * are pointers the form owns rather than values it merely suggests.
   */
  readonly logoAttachmentId?: string;
  readonly signatureAttachmentId?: string;
  /** 4 = financial year starts in April. */
  readonly fyStartMonth: number;
  readonly bankAccounts?: readonly BankAccount[];
  readonly documentSettings?: Partial<DocumentSettings>;
}

export type BusinessErrorCode =
  | 'NAME_REQUIRED'
  | 'STATE_REQUIRED'
  | 'STATE_INVALID'
  | 'GSTIN_INVALID'
  | 'GSTIN_STATE_MISMATCH'
  | 'FY_START_INVALID'
  | 'UNKNOWN_BUSINESS';

export class BusinessServiceError extends Error {
  constructor(
    readonly code: BusinessErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'BusinessServiceError';
  }
}

/** The stored form of the fields above, trimmed and upper-cased. */
export interface NormalisedBusinessProfile {
  readonly legalName: string;
  readonly tradeName?: string;
  readonly gstin?: string;
  readonly pan?: string;
  readonly stateCode: string;
  /** DERIVED from the code, never typed. See `normaliseBusinessProfile`. */
  readonly stateName: string;
  readonly addressLines: string[];
  readonly city: string;
  readonly district?: string;
  readonly pincode?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  readonly upiId?: string;
  readonly logoAttachmentId?: string;
  readonly signatureAttachmentId?: string;
  readonly fyStartMonth: number;
}

/** Trim, or `undefined` when the user left the field blank. */
function optional(value: string | undefined): string | undefined {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Validate and canonicalise a profile. Pure, so it is testable without a
 * database — and because every rule here protects something that only shows up
 * on a printed invoice.
 *
 * @throws BusinessServiceError with a code the screen can map to a field.
 */
export function normaliseBusinessProfile(input: BusinessProfileInput): NormalisedBusinessProfile {
  const legalName = (input.legalName ?? '').trim();
  if (legalName === '') {
    throw new BusinessServiceError(
      'NAME_REQUIRED',
      'A business needs a legal name — it prints on every invoice.',
    );
  }

  const stateCode = (input.stateCode ?? '').trim();
  if (stateCode === '') {
    throw new BusinessServiceError(
      'STATE_REQUIRED',
      'Choose the state your business is registered in. It decides whether invoices carry CGST+SGST or IGST.',
    );
  }
  if (!isValidStateCode(stateCode)) {
    throw new BusinessServiceError('STATE_INVALID', `"${stateCode}" is not a GST state code.`);
  }

  // Stored upper-cased, because a GSTIN is compared and printed as upper-case,
  // and a lower-case one on screen is the sign of a bad paste.
  const gstin = optional(input.gstin)?.toUpperCase();
  if (gstin !== undefined) {
    if (!isGstinShaped(gstin)) {
      throw new BusinessServiceError(
        'GSTIN_INVALID',
        `"${gstin}" is not a valid GSTIN. It should be 15 characters, like 29AAAAA1234A1Z5.`,
      );
    }
    if (gstin.slice(0, 2) !== stateCode) {
      throw new BusinessServiceError(
        'GSTIN_STATE_MISMATCH',
        `This GSTIN belongs to state ${gstin.slice(0, 2)} (${stateNameFor(gstin.slice(0, 2))}), ` +
          `but the business state is ${stateCode} (${stateNameFor(stateCode)}). An invoice would then ` +
          'carry the wrong tax split and the wrong state on its face.',
      );
    }
  }

  const fyStartMonth = Number(input.fyStartMonth);
  if (!Number.isInteger(fyStartMonth) || fyStartMonth < 1 || fyStartMonth > 12) {
    throw new BusinessServiceError('FY_START_INVALID', 'Choose the month the financial year starts in.');
  }

  return {
    legalName,
    tradeName: optional(input.tradeName),
    gstin,
    pan: optional(input.pan)?.toUpperCase(),
    stateCode,
    // Derived, not typed: a state name that disagrees with its own code is a
    // letterhead line reading "Telangana" under a Karnataka GSTIN.
    stateName: stateNameFor(stateCode),
    // Blank lines are dropped, so a stray empty row in the address box does not
    // print as a gap in the letterhead.
    addressLines: input.addressLines.map((l) => l.trim()).filter((l) => l !== ''),
    city: (input.city ?? '').trim(),
    district: optional(input.district),
    pincode: optional(input.pincode),
    phone: optional(input.phone),
    email: optional(input.email),
    website: optional(input.website),
    upiId: optional(input.upiId),
    // `optional` so '' becomes undefined, and undefined is what the stored row
    // means by "no logo" - the column is optional, not an empty string.
    logoAttachmentId: optional(input.logoAttachmentId),
    signatureAttachmentId: optional(input.signatureAttachmentId),
    fyStartMonth,
  };
}

export interface CreateBusinessInput extends BusinessProfileInput {
  readonly now: string;
  readonly deviceId: string;
}

export interface UpdateBusinessInput extends BusinessProfileInput {
  readonly id: string;
  readonly now: string;
  readonly deviceId: string;
}

/**
 * Create a business and record the mutation in the SAME transaction.
 *
 * The change id is minted by the recorder and stored on the outbox row, so a
 * retried create republishes the same id rather than producing a second company
 * on another device. See `recordBusinessMutation.ts` §10.
 */
export async function createBusiness(tx: BusinessTx, input: CreateBusinessInput): Promise<Business> {
  const profile = normaliseBusinessProfile(input);
  const id = ulid();

  const business: Business = {
    id,
    ...profile,
    country: 'India',
    bankAccounts: input.bankAccounts ? [...input.bankAccounts] : [],
    documentSettings: { ...DEFAULT_DOCUMENT_SETTINGS, ...(input.documentSettings ?? {}) },
    createdAt: input.now,
    updatedAt: input.now,
    syncVersion: 1,
  };

  await tx.businesses.put(business);
  await recordBusinessMutation(tx, {
    businessId: id,
    deviceId: input.deviceId,
    entityType: 'business',
    entityId: id,
    operation: 'UPSERT',
    version: 1,
    changedAt: input.now,
    payload: business as unknown as Record<string, unknown>,
  });
  return business;
}

/**
 * Edit a business.
 *
 * The version comes from the ROW, incremented, exactly as in `partyService`:
 * two devices editing the same company produce version 2 and version 2, and
 * `conflictModel.ts` can then tell a mergeable edit from a genuine clash. A
 * clock would make the edits look ordered by wall time, which lies the moment
 * one of those devices was asleep.
 */
export async function updateBusiness(tx: BusinessTx, input: UpdateBusinessInput): Promise<Business> {
  const existing = await tx.businesses.get(input.id);
  if (!existing) {
    throw new BusinessServiceError('UNKNOWN_BUSINESS', `No business with id "${input.id}".`);
  }

  const profile = normaliseBusinessProfile(input);
  const version = nextVersion(existing.syncVersion);
  const merged: Business = {
    ...existing,
    ...profile,
    // An absent field means "not supplied", NOT "clear it". This is the one
    // place that distinction is genuinely dangerous: the letterhead is read LIVE
    // from this row, so a profile form that submitted every empty input as
    // `undefined` would blank the phone number off the next invoice. Clearing a
    // field has to be an explicit, separate act.
    phone: profile.phone ?? existing.phone,
    email: profile.email ?? existing.email,
    // THESE TWO ARE THREE-STATE, AND THAT IS THE WHOLE POINT.
    //
    //   key absent   -> "not supplied", keep whatever is there
    //   ''           -> "clear it", store nothing
    //   'att-123'    -> "this is the image now"
    //
    // So the test is on the KEY, not on the value. Reading the normalised
    // profile instead would collapse "absent" and "''" into the same undefined
    // and silently pick one of them - and both wrong answers are bad:
    //
    //   * always-clear (what the value alone says) lets any caller that knows
    //     nothing about logos wipe a letterhead by saving an unrelated field -
    //     a sync apply, an import, a legacy migration;
    //   * always-keep makes a removed logo impossible to express at all, so
    //     'Remove' appears to work, saves, and the logo prints forever.
    //
    // The profile form always sends both keys, which is what makes '' reachable
    // in practice while keeping the absent case safe for everyone else.
    ...('logoAttachmentId' in input
      ? { logoAttachmentId: profile.logoAttachmentId }
      : { logoAttachmentId: existing.logoAttachmentId }),
    ...('signatureAttachmentId' in input
      ? { signatureAttachmentId: profile.signatureAttachmentId }
      : { signatureAttachmentId: existing.signatureAttachmentId }),
    bankAccounts: input.bankAccounts ? [...input.bankAccounts] : existing.bankAccounts,
    documentSettings: { ...existing.documentSettings, ...(input.documentSettings ?? {}) },
    // Never rewritten by a profile edit: it is when the row was first created,
    // and a migration backfill may carry an earlier truth.
    createdAt: existing.createdAt,
    updatedAt: input.now,
    syncVersion: version,
  };

  await tx.businesses.put(merged);
  await recordBusinessMutation(tx, {
    businessId: merged.id,
    deviceId: input.deviceId,
    entityType: 'business',
    entityId: merged.id,
    operation: 'UPSERT',
    version,
    changedAt: input.now,
    payload: merged as unknown as Record<string, unknown>,
  });
  return merged;
}
